// The Salesmen tab: results and follow-through per salesman, with a panel for each.
import { useMemo, useState } from "react";
import { getSalesmanScorecard } from "../../distflowAPI/salesAnalyticsApi";
import SalesmanPanel from "./SalesmanPanel";
import SplitView from "./SplitView";
import { plural } from "./customerText";
import { count, money, pct, signedPct, tone } from "./format";
import useSideReport from "./useSideReport";
import "./SalesmanScorecard.css";

export const FLAG_LABELS = {
  low_coverage: "Few accounts ordering",
  losing_customers: "Losing customers",
  shrinking_accounts: "Accounts shrinking",
  unattended_risk: "At-risk customers without follow-up",
  no_new_business: "No new business",
  no_tasks: "No tasks logged",
};

const COLUMNS = [
  { key: "sales", label: "Sales", value: (row) => Number(row.sales) },
  { key: "coverage_percent", label: "Accounts ordering", value: (row) => Number(row.coverage_percent ?? -1) },
  { key: "retention_percent", label: "Kept customers", comparison: true, value: (row) => Number(row.retention_percent ?? -1) },
  { key: "at_risk_weekly", label: "At risk", value: (row) => Number(row.at_risk_weekly) },
  { key: "tasks_logged", label: "Tasks", secondary: true, value: (row) => row.tasks_logged ?? -1 },
];

function Meter({ value, median, low }) {
  if (value == null) return null;
  return <span className="sa-meter" aria-hidden="true">
    <b className={low ? "is-low" : ""} style={{ width: `${Math.min(100, Number(value))}%` }} />
    {median != null && <i style={{ left: `${Math.min(100, Number(median))}%` }} />}
  </span>;
}

export default function SalesmanScorecard({ request, openKey, onOpen, onClose, onOpenCustomer, onOpenProduct, onFocusSalesman, scope }) {
  const [sort, setSort] = useState({ key: "sales", direction: "desc" });
  const [attempt, setAttempt] = useState(0);
  const params = useMemo(() => ({
    start_date: request?.start_date, end_date: request?.end_date, territory_id: request?.territory_id,
    comparison: request?.comparison, comparison_start_date: request?.comparison_start_date, comparison_end_date: request?.comparison_end_date,
  }), [request]);
  // Team-wide on purpose: the scorecard ignores the salesman and focus filters.
  const scorecard = useSideReport(getSalesmanScorecard, params, Boolean(request?.start_date), attempt);
  const data = scorecard.data;
  const hasComparison = Boolean(data?.comparison);
  const columns = COLUMNS.filter((column) => !column.comparison || hasComparison);
  const medians = data?.team?.medians || {};

  const rows = useMemo(() => {
    const column = COLUMNS.find((item) => item.key === sort.key) || COLUMNS[0];
    return [...(data?.rows || [])].sort((a, b) => {
      if (a.is_office !== b.is_office) return a.is_office ? 1 : -1;
      const difference = column.value(a) - column.value(b);
      return (sort.direction === "asc" ? difference : -difference) || a.name.localeCompare(b.name);
    });
  }, [data, sort]);
  const openRow = rows.find((row) => row.key === openKey) || null;
  const toggleSort = (key) => setSort((current) => (current.key === key
    ? { key, direction: current.direction === "desc" ? "asc" : "desc" } : { key, direction: "desc" }));
  const panel = openRow ? (mode) => <SalesmanPanel key={openRow.key} mode={mode} row={openRow} team={data.team} comparison={data.comparison}
    request={request} scope={scope} onClose={onClose} onOpenCustomer={onOpenCustomer} onOpenProduct={onOpenProduct} onFocus={onFocusSalesman} /> : null;

  return <SplitView panel={panel}>
    <section className="sa-list salesmen" aria-label="Salesmen" aria-busy={scorecard.loading}>
      <div className="sa-list-head">
        <h2>Salesmen</h2>
        <p>Sales follow each order’s salesman; everything else is measured on each salesman’s book, the active accounts assigned to them.
          Visits and calls aren’t recorded, so this shows results and follow-through, not effort.
          {data && !data.team?.tasks_in_use && " No account tasks were logged in these dates."}</p>
      </div>
      {scorecard.failed && <p className="sa-note" role="alert">The scorecard could not be loaded.
        <button type="button" className="sa-link-button" onClick={() => setAttempt(attempt + 1)}>Try again</button></p>}
      {!data && scorecard.loading && <p className="sa-list-empty">Scoring the team…</p>}
      {data && (rows.length ? <div className="sa-table-scroll"><table className="sa-list-table" aria-label="Salesman scorecard">
        <thead><tr>
          <th scope="col" className="sa-col-name">Salesman</th>
          {columns.map((column) => <th scope="col" key={column.key} className={`${column.key === "sales" ? "sa-num" : ""}${column.secondary ? " sa-col-secondary" : ""}`}
            aria-sort={sort.key === column.key ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>
            <button type="button" onClick={() => toggleSort(column.key)}>{column.label}<span aria-hidden="true">{sort.key === column.key ? (sort.direction === "asc" ? "↑" : "↓") : ""}</span></button>
          </th>)}
        </tr></thead>
        <tbody>{rows.map((row) => {
          const flags = row.flags || [];
          return <tr key={row.key} className={`${openKey === row.key ? "is-open" : ""}${row.is_office ? " is-muted" : ""}`} onClick={() => onOpen?.(row.key)}>
            <th scope="row" className="sa-col-name">
              <button type="button" className="sa-row-link" aria-current={openKey === row.key ? "true" : undefined} aria-controls="sa-detail-panel"
                onClick={(event) => { event.stopPropagation(); onOpen?.(row.key); }}>{row.name}</button>
              {row.is_office ? <small>Accounts with no salesman</small>
                : flags.length > 0 ? <small className="sa-warn" title={flags.map((flag) => FLAG_LABELS[flag] || flag).join(", ")}>
                  {flags.slice(0, 2).map((flag) => FLAG_LABELS[flag] || flag).join(" · ")}{flags.length > 2 ? ` +${flags.length - 2}` : ""}</small>
                  : <small>Nothing stands out</small>}
            </th>
            <td className="sa-num" data-label="Sales">{money(row.sales)}
              {hasComparison
                ? <small className={`sa-${tone(row.sales_change_percent) === "gain" ? "good" : tone(row.sales_change_percent) === "decline" ? "bad" : "muted"}`}>{signedPct(row.sales_change_percent) ?? "no baseline"}</small>
                : <small>{count(row.orders)} {plural(row.orders, "order")}</small>}</td>
            <td data-label="Accounts ordering">{row.book_accounts ? <>{pct(row.coverage_percent, 0)}
              <Meter value={row.coverage_percent} median={row.is_office ? null : medians.coverage_percent} low={flags.includes("low_coverage")} />
              <small>{count(row.active_book_accounts)} of {count(row.book_accounts)}</small></> : "—"}</td>
            {hasComparison && <td data-label="Kept customers">{row.previous_book_customers ? <>{pct(row.retention_percent, 0)}
              <Meter value={row.retention_percent} median={row.is_office ? null : medians.retention_percent} low={flags.includes("losing_customers")} />
              <small>{count(row.retained_customers)} of {count(row.previous_book_customers)}</small></> : "—"}</td>}
            <td data-label="At risk">{row.at_risk_customers ? <>{count(row.at_risk_customers)} {plural(row.at_risk_customers, "customer")}
              <small className={flags.includes("unattended_risk") ? "sa-warn" : ""}>{money(row.at_risk_weekly)}/wk · {row.at_risk_without_follow_up} no task</small></> : <span className="sa-muted">None</span>}</td>
            <td className="sa-col-secondary" data-label="Tasks">{row.tasks_logged == null ? <><span className="sa-muted">—</span><small>not a salesman</small></>
              : <>{count(row.tasks_logged)} logged<small className={flags.includes("no_tasks") || row.tasks_overdue ? "sa-warn" : ""}>{count(row.tasks_completed)} done{row.tasks_overdue ? ` · ${row.tasks_overdue} overdue` : ""}</small></>}</td>
          </tr>;
        })}</tbody>
      </table></div> : <p className="sa-list-empty">No salesman activity in these dates.</p>)}
      {data && rows.length > 0 && <div className="sa-list-foot"><span>
        Black tick: team median ({pct(medians.coverage_percent, 0)} of accounts ordering{hasComparison && medians.retention_percent != null ? `, ${pct(medians.retention_percent, 0)} of customers kept` : ""}). Click a salesman for what stands out, their call list and what to push.
      </span></div>}
    </section>
  </SplitView>;
}
